import PropTypes from 'prop-types'
import React from 'react'

import ACol from './ACol'
import ARow from './ARow'
import Cont from './Cont'

const MAX_CHILDREN_PER_ROW = 4
const BOOTSTRAP_COLS_PER_ROW = 12
const MINIMUM_NUM_COLS = BOOTSTRAP_COLS_PER_ROW / MAX_CHILDREN_PER_ROW

/**
 * @author Angelo Natal <Angelo.Natal@aspiraconnect.com>
 *
 * AlignMultiCols component to separate children with a vertical grey bar. The columns content are aligned and the will have equal height.
 * The width of the children is determined by the number of children i.e. if you have two children, they take up 50% each, 3 children take up 33% each, etc.
 * Maximum children per row is 4 (25% each), if you pass more than 4 children, a new row will be created.  You can also specify a custom width for each resolution.
 * For example, on large screen with only two children, taking up 50% of the screen can be a bit much depending on the content of the children which can result
 * in the separator bar being way too far to the right of the first child.  In this case, you can specify a custom number of columns that each child should take
 * up so that they appear closer together.
 *
 * @returns {element} AlignMultiCols
 *
 */
const AlignMultiCols = ({
  children,
  colWidthOverrideXS = 0,
  colWidthOverrideSM = 0,
  colWidthOverrideMD = 0,
  colWidthOverrideLG = 0,
}) => {
  let defaultNumCols = BOOTSTRAP_COLS_PER_ROW / children.length
  let formattedChildren = null
  if (defaultNumCols < MINIMUM_NUM_COLS) {
    defaultNumCols = MINIMUM_NUM_COLS
  }

  const numColsXS =
    colWidthOverrideXS === 0 ? defaultNumCols : colWidthOverrideXS
  const numColsSM =
    colWidthOverrideSM === 0 ? defaultNumCols : colWidthOverrideSM
  const numColsMD =
    colWidthOverrideMD === 0 ? defaultNumCols : colWidthOverrideMD
  const numColsLG =
    colWidthOverrideLG === 0 ? defaultNumCols : colWidthOverrideLG

  const getRow = function (firstColumnIndex) {
    const childArray = []
    for (
      let i = firstColumnIndex;
      i < firstColumnIndex + MAX_CHILDREN_PER_ROW;
      i++
    ) {
      if (typeof children[i] !== 'undefined') {
        childArray.push(children[i])
      }
    }

    const ui = childArray.map((child, childIndex) => (
      <ACol
        key={childIndex}
        xs={numColsXS}
        sm={numColsSM}
        md={numColsMD}
        lg={numColsLG}
        bsPrefix={'col'}
        style={'align-multi-cols-column '}
      >
        {child}
      </ACol>
    ))

    return (
      <ARow
        key={(firstColumnIndex - MAX_CHILDREN_PER_ROW) / MAX_CHILDREN_PER_ROW}
        bsPrefix={'row align-multi-cols'}
      >
        {ui}
      </ARow>
    )
  }

  formattedChildren = children.map((child, index) => {
    let ui = null
    if (index === 0 || index % MAX_CHILDREN_PER_ROW === 0) {
      ui = getRow(index)
    }

    return ui
  })

  return <Cont>{formattedChildren}</Cont>
}

AlignMultiCols.propTypes = {
  /** array of children which will be separated by a vertical grey bar */
  children: PropTypes.array.isRequired,
  /** override the number of columns used for each child on extra small/mobile screens */
  colWidthOverrideXS: PropTypes.number,
  /** override the number of columns used for each child on small screens */
  colWidthOverrideSM: PropTypes.number,
  /** override the number of columns used for each child on medium screens */
  colWidthOverrideMD: PropTypes.number,
  /** override the number of columns used for each child on large screens */
  colWidthOverrideLG: PropTypes.number,
}

export default AlignMultiCols
